import * as React from 'react'
import { StyleSheet, View, Text } from 'react-native'
import { useRouter } from 'expo-router'
import { Pressable } from 'react-native-gesture-handler'

import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { DS } from '../../design-system'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../models'
import { UserModel, UserModelType } from '../../models/api-store'
import { getSnapshot, SnapshotOut } from 'mobx-state-tree'
import { monthDayAndTimeFormatterWithYear } from '../../utils'

type UserType = SnapshotOut<typeof UserModel.Type>
const columnHelper = createColumnHelper<UserType>()

const UserNameCell = ({ user }: { user: UserType }) => {
  const router = useRouter()

  return (
    <Pressable onPress={() => router.push(`/dashboard/users/${user._id}`)} style={{ alignItems: 'flex-start' }}>
      <DS.Text variant="Body Small/Medium" color="Admin/Icon">
        {user.name}
      </DS.Text>
    </Pressable>
  )
}

const UserEmailCell = ({ email }: { email?: string | null }) => {
  return <DS.Text variant="Body Small/Regular">{email || 'N/A'}</DS.Text>
}

const UserPhoneCell = ({ phone }: { phone?: string | null }) => {
  return <DS.Text variant="Body Small/Regular">{phone || 'N/A'}</DS.Text>
}

const UserRolesCell = ({ roles }: { roles?: string[] }) => {
  if (!roles || roles.length === 0) {
    return <DS.Text variant="Body Small/Regular">User</DS.Text>
  }

  return (
    <View style={styles.rolesContainer}>
      {roles.map((role, index) => (
        <View key={index} style={styles.roleBadge}>
          <DS.Text variant="Body Small/Regular" color="Admin/Icon">
            {role}
          </DS.Text>
        </View>
      ))}
    </View>
  )
}

const columns = [
  columnHelper.accessor('name', {
    header: () => 'Name',
    cell: (info) => <UserNameCell user={info.row.original} />,
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('email', {
    header: () => 'Email',
    cell: (info) => <UserEmailCell email={info.getValue()} />,
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('phoneNumber', {
    header: () => 'Phone',
    cell: (info) => <UserPhoneCell phone={info.getValue()} />,
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('userRoles', {
    header: () => 'Roles',
    cell: (info) => <UserRolesCell roles={info.getValue() as string[]} />,
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('lastLogin', {
    header: () => 'Last Login',
    cell: (info) => (
      <DS.Text selectable variant="Body Small/Regular">
        {info.getValue() ? monthDayAndTimeFormatterWithYear(new Date(info.getValue() as any)) : 'N/A'}
      </DS.Text>
    ),
    footer: (info) => info.column.id
  }),
  columnHelper.accessor('createdAt', {
    header: () => 'Created At',
    cell: (info) => (
      <DS.Text selectable variant="Body Small/Regular">
        {info.getValue() ? monthDayAndTimeFormatterWithYear(new Date(info.getValue() as any)) : 'N/A'}
      </DS.Text>
    ),
    footer: (info) => info.column.id
  })
]

interface UsersTableProps {
  users: UserModelType[]
  isLoading?: boolean
}

export const UsersTable = observer(({ users, isLoading = false }: UsersTableProps) => {
  const tableData = React.useMemo(() => {
    return users && users.length > 0 ? users.map((user) => getSnapshot(user)) : []
  }, [users])

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  if (isLoading) {
    return (
      <View style={styles.container}>
        <DS.Text variant="Heading 3/Medium">Users</DS.Text>
        <View style={styles.loadingContainer}>
          <DS.Text variant="Body Medium/Regular">Loading...</DS.Text>
        </View>
      </View>
    )
  }

  if (!users || users.length === 0) {
    return (
      <View style={styles.container}>
        <DS.Text variant="Heading 3/Medium">Users</DS.Text>
        <View style={styles.emptyContainer}>
          <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey">
            No users found. Try a different search term.
          </DS.Text>
        </View>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <DS.Text variant="Heading 3/Medium">Users ({users.length})</DS.Text>
      <View style={styles.tableContainer}>
        <View style={styles.tableHeader}>
          {table.getHeaderGroups().map((headerGroup) => (
            <View style={styles.tableRow} key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <View style={styles.tableHeaderCell} key={header.id}>
                  {header.isPlaceholder ? null : (
                    <Text style={styles.headerText}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Text>
                  )}
                </View>
              ))}
            </View>
          ))}
        </View>
        <View style={styles.tableBody}>
          {table.getRowModel().rows.map((row) => (
            <View style={styles.tableRow} key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <View style={styles.tableCell} key={cell.id}>
                  <Text style={styles.cellText}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      </View>
    </View>
  )
})

const styles = StyleSheet.create({
  container: {
    padding: 24,
    marginBottom: 32,
    borderRadius: 12,
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1,
    backgroundColor: DS.palette['Global/White']
  },
  tableContainer: {
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    marginTop: 16,
    marginBottom: 16,
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1
  },
  tableHeader: {
    backgroundColor: '#eeedea'
  },
  tableBody: {
    backgroundColor: 'white'
  },
  tableRow: {
    flexDirection: 'row'
  },
  tableHeaderCell: {
    flex: 1,
    borderBottomWidth: 2,
    borderBottomColor: '#dee2e6',
    padding: 16
  },
  headerText: {
    fontWeight: '600',
    color: DS.palette['Brand/Dark Grey']
  },
  tableCell: {
    flex: 1,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef'
  },
  cellText: {
    color: '#212529'
  },
  rolesContainer: {
    gap: 4,
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  roleBadge: {
    backgroundColor: '#e3f2fd',
    color: DS.palette['Admin/Icon'],
    padding: 8,
    borderRadius: 99,
    borderColor: '#90caf9',
    borderWidth: 1
  },
  loadingContainer: {
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center'
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center'
  }
})
